import { useCallback, useEffect, useState } from "react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Wallet, Trophy, Users, UserPlus, Target, Briefcase, Store, AlarmClock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { php, phpShort } from "@/lib/format";
import { PageHeader, MarketSelect, Panel, SelectBox } from "@/components/common/ui";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const COLORS = ["#0B192C", "#F97316", "#64748B", "#3B82F6", "#10B981", "#F59E0B", "#94A3B8"];
const KPI = {
  total_leads: { label: "Total leads", icon: Users },
  new_this_month: { label: "New leads this month", icon: UserPlus },
  qualified: { label: "Qualified leads", icon: Target },
  open_opportunities: { label: "Open opportunities", icon: Briefcase },
  pipeline_value: { label: "Total pipeline value", icon: Wallet, money: true, note: "Estimated · not yet won" },
  won_value: { label: "Won sales value", icon: Trophy, money: true, note: "Actual closed sales", won: true },
  conversion_rate: { label: "Conversion rate", icon: TrendingUp, pct: true, note: "Won ÷ total leads" },
  active_dealers: { label: "Active dealers", icon: Store },
  overdue_followups: { label: "Overdue follow-ups", icon: AlarmClock, alert: true },
};

export function dateRange(preset, custom) {
  const now = new Date();
  const start = (d) => d.toISOString();
  if (preset === "month") return { date_from: start(new Date(now.getFullYear(), now.getMonth(), 1)) };
  if (preset === "90d") return { date_from: start(new Date(Date.now() - 90 * 86400000)) };
  if (preset === "ytd") return { date_from: start(new Date(now.getFullYear(), 0, 1)) };
  if (preset === "custom") return { date_from: custom.from ? new Date(custom.from).toISOString() : undefined, date_to: custom.to ? new Date(custom.to + "T23:59:59").toISOString() : undefined };
  return {};
}

export function DateFilter({ preset, setPreset, custom, setCustom }) {
  return (
    <>
      <SelectBox value={preset} onChange={setPreset} testid="date-filter" allLabel="All time"
        options={[{ value: "month", label: "This month" }, { value: "90d", label: "Last 90 days" }, { value: "ytd", label: "Year to date" }, { value: "custom", label: "Custom range" }]} />
      {preset === "custom" && (
        <>
          <Input type="date" className="w-40 bg-white" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="date-from-input" />
          <Input type="date" className="w-40 bg-white" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="date-to-input" />
        </>
      )}
    </>
  );
}

function KpiCard({ k, value }) {
  const c = KPI[k];
  const display = c.money ? phpShort(value) : c.pct ? `${value}%` : Number(value).toLocaleString("en-PH");
  const tone = c.won ? "border-t-emerald-500" : c.money ? "border-t-[#0B192C] border-dashed" : c.alert && value > 0 ? "border-t-red-500" : "border-t-orange-500";
  return (
    <div className={`bg-white border border-slate-200 border-t-[3px] ${tone} rounded-md p-5 transition-shadow hover:shadow-md`} data-testid={`kpi-${k.replace(/_/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{c.label}</span>
        <c.icon className={`h-4 w-4 ${c.alert && value > 0 ? "text-red-500" : "text-slate-400"}`} />
      </div>
      <div className={`num text-3xl font-bold mt-3 tracking-tight ${c.won ? "text-emerald-700" : c.alert && value > 0 ? "text-red-600" : "text-[#0B192C]"}`} title={c.money ? php(value) : undefined} data-testid={`kpi-${k.replace(/_/g, "-")}-value`}>{display}</div>
      {c.note && <div className="text-[11px] text-slate-400 mt-1.5">{c.note}</div>}
    </div>
  );
}

const moneyTip = (v) => php(v);

export default function Dashboard() {
  const { meta } = useAuth();
  const [market, setMarket] = useState("");
  const [preset, setPreset] = useState("");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [funnelMarket, setFunnelMarket] = useState("dealer");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard", { params: { market: market || undefined, ...dateRange(preset, custom) } });
      setData(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [market, preset, custom]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (market) setFunnelMarket(market); }, [market]);

  if (!data) return <div className="text-slate-400" data-testid="dashboard-loading">Loading dashboard…</div>;
  const kpis = (data.visible_kpis?.length ? data.visible_kpis : Object.keys(KPI)).filter((k) => KPI[k]);
  const funnel = data.funnel[funnelMarket] || [];

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Executive overview" title="Dashboard" subtitle={`Calculated live from stored records${data.scope === "own" ? " · showing your assigned leads only" : ""}. Pipeline values are estimates; won sales are actual closed orders.`}>
        <MarketSelect value={market} onChange={setMarket} />
        <DateFilter preset={preset} setPreset={setPreset} custom={custom} setCustom={setCustom} />
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4 mb-6">
        {kpis.map((k) => <KpiCard key={k} k={k} value={data.kpis[k]} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Leads by market" testid="chart-leads-by-market">
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.by_market} dataKey="count" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {data.by_market.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip />
                <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Sales funnel conversion by stage" className="lg:col-span-2" testid="chart-funnel-conversion"
          action={!market && <SelectBox value={funnelMarket} onChange={(v) => setFunnelMarket(v || "dealer")} testid="funnel-chart-market" className="w-48 h-8" options={meta.markets.map((m) => ({ value: m.key, label: m.name }))} />}>
          <div className="space-y-2">
            {funnel.map((s) => (
              <div key={s.stage_id} className="grid grid-cols-[150px_1fr_90px] sm:grid-cols-[200px_1fr_110px] items-center gap-3 text-sm">
                <span className="truncate text-slate-600">{s.name}</span>
                <div className="h-6 bg-slate-100 rounded-sm overflow-hidden">
                  <div className={`h-full ${s.type === "won" ? "bg-emerald-500" : s.type === "lost" ? "bg-red-300" : "bg-[#0B192C]"} transition-all duration-700`} style={{ width: `${Math.max(s.pct, 1)}%` }} />
                </div>
                <span className="num text-xs text-right text-slate-500">{s.reached} · {s.pct}%</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Bar = share of leads (in filter) that reached each stage.</p>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Panel title="Monthly won sales (actual)" testid="chart-monthly-won">
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={data.monthly_won}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={phpShort} tick={{ fontSize: 11 }} width={70} />
                <Tooltip formatter={moneyTip} />
                <Area dataKey="value" name="Won sales" stroke="#10B981" fill="#10B98133" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Leads by source" testid="chart-leads-by-source">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.by_source}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="source" tick={{ fontSize: 11 }} interval={0} angle={-15} height={45} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" name="Leads" fill="#0B192C" radius={[2, 2, 0, 0]} />
                <Bar dataKey="won" name="Won" fill="#F97316" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <Panel title="Sales performance by assigned executive" testid="chart-sales-by-executive">
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={data.by_executive} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tickFormatter={phpShort} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 11 }} />
              <Tooltip formatter={moneyTip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="pipeline" name="Open pipeline (estimate)" fill="#94A3B8" />
              <Bar dataKey="won_value" name="Won sales (actual)" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
}
